import { Box, Collapse, type CollapseProps, Fade } from '@mui/material';
import { type ReactNode } from 'react';

/** How long a chip's width takes to open or close. */
export const JOB_TAG_TRANSITION_MS = 200;
// The fade runs longer than the width and starts a beat after it. Timed
// together, the chip is still clipped to a sliver while its opacity climbs, so
// the fade is over before there is anything to see fading.
const TAG_FADE_IN_MS = 350;
const TAG_FADE_IN_DELAY_MS = 100;

interface JobTagTransitionProps
  extends Omit<CollapseProps, 'children' | 'orientation' | 'timeout'> {
  children: ReactNode;
  gap: number;
  timeout: number;
}

/**
 * One chip's way in and out: its width opens or closes while it fades.
 *
 * `TransitionGroup` hands `in`, `onExited` and friends to its direct children,
 * so this takes them and passes them on to the `Collapse`.
 *
 * The space between chips lives INSIDE the collapsing box (`pr`), with a
 * matching negative margin cancelling the parent's own flex gap. A gap left to
 * the parent stays open around a chip that has shrunk to nothing, then snaps
 * shut when it unmounts.
 *
 * The box may SHRINK. A tag name has no length limit, and a wrapper that
 * holds its content width pushes a long one out of the sidebar; `minWidth: 0`
 * down the chain is what lets the chip's own ellipsis take over instead.
 * @param root0
 * @param root0.children
 * @param root0.gap
 * @param root0.timeout
 */
export function JobTagTransition({
  children,
  gap,
  timeout,
  in: shown = undefined,
  ...transitionProps
}: JobTagTransitionProps) {
  return (
    <Collapse
      {...transitionProps}
      in={shown}
      orientation="horizontal"
      timeout={timeout}
      sx={{
        mr: -gap,
        minWidth: 0,
        maxWidth: '100%',
        '& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner': {
          minWidth: 0,
          maxWidth: '100%',
        },
      }}
    >
      {/* `appear={false}`: Fade appears by default, and inside a
          TransitionGroup that is still mounting it uses its own `appear` —
          so every tag already on the job faded in on each page load. A chip
          added later mounts into a settled group, which has it enter. */}
      <Fade
        in={shown}
        appear={false}
        timeout={{ enter: timeout && TAG_FADE_IN_MS, exit: timeout }}
        style={{
          transitionDelay: `${shown && timeout ? TAG_FADE_IN_DELAY_MS : 0}ms`,
        }}
      >
        <Box sx={{ display: 'flex', minWidth: 0, maxWidth: '100%', pr: gap }}>
          {children}
        </Box>
      </Fade>
    </Collapse>
  );
}
